import React, { useState, useEffect } from 'react';
import { X, Upload, Image, Sliders, Trash2, Check, X as XIcon, Palette } from 'lucide-react';
import { ColorPicker } from 'antd';

const BackgroundSettingsModal = ({
  isOpen,
  onClose,
  backgroundImage,
  backgroundOpacity,
  backgroundBlur,
  backgroundMode,
  backgroundColor,
  onBackgroundImageChange,
  onBackgroundOpacityChange,
  onBackgroundBlurChange,
  onBackgroundModeChange,
  onBackgroundColorChange,
  onClearBackground,
  onSave
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [tempBackgroundImage, setTempBackgroundImage] = useState(backgroundImage);
  const [tempBackgroundOpacity, setTempBackgroundOpacity] = useState(backgroundOpacity);
  const [tempBackgroundBlur, setTempBackgroundBlur] = useState(backgroundBlur);
  const [tempBackgroundMode, setTempBackgroundMode] = useState(backgroundMode);
  const [tempBackgroundColor, setTempBackgroundColor] = useState(backgroundColor);
  
  // 当props变化时更新临时状态
  useEffect(() => {
    setTempBackgroundImage(backgroundImage);
    setTempBackgroundOpacity(backgroundOpacity);
    setTempBackgroundBlur(backgroundBlur);
    setTempBackgroundMode(backgroundMode);
    setTempBackgroundColor(backgroundColor);
  }, [backgroundImage, backgroundOpacity, backgroundBlur, backgroundMode, backgroundColor]);
  
  if (!isOpen) return null;

  const handleOpacityChange = (e) => {
    setTempBackgroundOpacity(parseFloat(e.target.value));
  };

  const handleBlurChange = (e) => {
    setTempBackgroundBlur(parseFloat(e.target.value));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 检查文件类型
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('请选择有效的图片文件（JPG、PNG、GIF、WebP）');
        e.target.value = '';
        return;
      }
      
      // 检查文件大小（限制为5MB）
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('图片文件大小不能超过5MB');
        e.target.value = '';
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setTempBackgroundImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      
      // 检查文件类型
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('请选择有效的图片文件（JPG、PNG、GIF、WebP）');
        return;
      }
      
      // 检查文件大小（限制为5MB）
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('图片文件大小不能超过5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setTempBackgroundImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    onBackgroundImageChange(tempBackgroundImage);
    onBackgroundOpacityChange(tempBackgroundOpacity);
    onBackgroundBlurChange(tempBackgroundBlur);
    onBackgroundModeChange(tempBackgroundMode);
    onBackgroundColorChange(tempBackgroundColor);
    onSave && onSave();
    onClose();
  };

  const handleCancel = () => {
    setTempBackgroundImage(backgroundImage);
    setTempBackgroundOpacity(backgroundOpacity);
    setTempBackgroundBlur(backgroundBlur);
    setTempBackgroundMode(backgroundMode);
    setTempBackgroundColor(backgroundColor);
    onClose();
  };

  const handleClearBackground = () => {
    setTempBackgroundImage('');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-md relative" onClick={(e) => e.stopPropagation()}>
        {/* 关闭按钮在右上角 */}
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 rounded-full hover:bg-gray-100 z-10 flex items-center justify-center"
        >
          <X size={18} />
        </button>
        
        <div className="modal-header">
          <h3 className="text-lg font-semibold text-gray-900">背景设置</h3>
        </div>
        
        <div className="modal-body">
          {/* 背景模式选择 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              背景模式
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setTempBackgroundMode('image')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                  tempBackgroundMode === 'image'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50'
                }`}
              >
                <Image size={18} />
                背景图片
              </button>
              <button
                type="button"
                onClick={() => setTempBackgroundMode('color')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                  tempBackgroundMode === 'color'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:bg-primary-50'
                }`}
              >
                <Palette size={18} />
                纯色背景
              </button>
            </div>
          </div>

          {/* 背景图片上传 */}
          {tempBackgroundMode === 'image' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Upload size={16} className="mr-2" />
                背景图片
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  id="background-upload"
                />
                <label
                  htmlFor="background-upload"
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${
                    isDragOver 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-primary-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload 
                      size={32} 
                      className={`mb-2 transition-colors duration-200 ${
                        isDragOver ? 'text-primary-500' : 'text-gray-400'
                      }`} 
                    />
                    <p className={`text-sm mb-1 transition-colors duration-200 ${
                      isDragOver ? 'text-primary-600' : 'text-gray-500'
                    }`}>
                      <span className={`font-semibold transition-colors duration-200 ${
                        isDragOver ? 'text-primary-700' : 'text-primary-600 hover:text-primary-500'
                      }`}>
                        {isDragOver ? '松开鼠标上传' : '点击上传'}
                      </span>
                      {!isDragOver && '或拖拽文件到此处'}
                    </p>
                    <p className={`text-xs transition-colors duration-200 ${
                      isDragOver ? 'text-primary-500' : 'text-gray-400'
                    }`}>
                      支持 PNG, JPG, GIF, WebP 格式
                    </p>
                    <p className={`text-xs transition-colors duration-200 ${
                      isDragOver ? 'text-primary-500' : 'text-gray-400'
                    }`}>
                      文件大小限制：5MB
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {tempBackgroundMode === 'image' && tempBackgroundImage && (
            <>
              {/* 背景预览 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Image size={16} className="mr-2" />
                  背景预览
                </label>
                <div className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-300">
                  <img
                    src={tempBackgroundImage}
                    alt="背景预览"
                    className="w-full h-full object-cover"
                    style={{
                      filter: `blur(${tempBackgroundBlur}px)`,
                      opacity: tempBackgroundOpacity
                    }}
                  />
                </div>
              </div>

              {/* 透明度设置 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Sliders size={16} className="mr-2" />
                  透明度: {Math.round(tempBackgroundOpacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={tempBackgroundOpacity}
                  onChange={handleOpacityChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* 模糊度设置 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Sliders size={16} className="mr-2" />
                  模糊度: {tempBackgroundBlur}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="20"
                  step="1"
                  value={tempBackgroundBlur}
                  onChange={handleBlurChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0px</span>
                  <span>20px</span>
                </div>
              </div>

              {/* 清除背景按钮 */}
              <div className="flex justify-center mb-6">
                <button
                  onClick={handleClearBackground}
                  className="btn btn-danger"
                >
                  <Trash2 size={16} className="mr-2" />
                  清除背景
                </button>
              </div>
            </>
          )}

          {/* 纯色背景选择 */}
          {tempBackgroundMode === 'color' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Palette size={16} className="mr-2" />
                背景颜色
              </label>
              <div className="flex items-center gap-3">
                <ColorPicker
                  value={tempBackgroundColor}
                  onChange={(color) => setTempBackgroundColor(color.toHexString())}
                  showText
                  size="large"
                />
                <div className="flex-1">
                  <input
                    type="text"
                    value={tempBackgroundColor}
                    onChange={(e) => setTempBackgroundColor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
              <div className="mt-3 p-4 rounded-lg border border-gray-200" style={{ backgroundColor: tempBackgroundColor }}>
                <p className="text-sm text-gray-600 text-center">颜色预览</p>
              </div>
            </div>
          )}
        </div>
        
        {/* 保存和取消按钮 */}
        <div className="modal-footer">
          <button
            onClick={handleSave}
            className="btn btn-primary"
          >
            <Check size={16} className="mr-2" />
            保存
          </button>
          <button
            onClick={handleCancel}
            className="btn btn-danger"
          >
            <XIcon size={16} className="mr-2" />
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackgroundSettingsModal;
